import { GEMINI_CONFIG } from '../apiConfig';
import type { WorkoutActionChipCategory } from '@/types';
import enLocale from './locales/en.json';
import plLocale from './locales/pl.json';

export interface TranslationKeys {
  title: string;
  subtitle: string;
  appSubtitle: string;
  /** Premium tagline displayed under the Prompt2Train logo in the header. */
  appTagline: string;
  inputLabel: string;
  zonesTitle: string;
  runZonesTitle: string;
  placeholder: string;
  /** Single-line textarea hint (replaces rotating placeholder hints). */
  workoutPromptPlaceholder: string;
  runPlaceholder: string;
  example: string;
  runExample: string;
  generateBtn: string;
  generatingBtn: string;
  generationStatusOrchestrating: string;
  generationStatusVerifying: string;
  downloadBtn: string;
  /** Short note under .ZWO download — Zwift-only; Garmin/Polar use .FIT. */
  downloadZwoFormatHint: string;
  sendToTPBtn: string;
  sendToGarminBtn: string;
  sendToPolarFlowBtn: string;
  /** Nominative destination line under export buttons (brand-safe icons + plain names). */
  exportCaptionTrainingPeaks: string;
  exportCaptionGarminConnect: string;
  exportCaptionPolarFlow: string;
  garminInstruction: string;
  garminExportSuccess: string;
  openGarminConnectBtn: string;
  garminBikeOnlyToast: string;
  downloadFitBtn: string;
  shareWorkoutDescriptionBtn: string;
  mobileGarminFitHint: string;
  fitBikeOnlyToast: string;
  fitFtpModalTitle: string;
  fitFtpModalHint: string;
  fitFtpPlaceholder: string;
  fitFtpConfirm: string;
  fitFtpCancel: string;
  fitFtpInvalid: string;
  fitFtpFallbackToast: string;
  fitFtpExportBlocked: string;
  fitFtpSavePermanentLabel: string;
  userFtpLabel: string;
  userFtpTooltip: string;
  userFtpSaveError: string;
  userFtpInvalid: string;
  visualizerTitle: string;
  outputTitleBike: string;
  outputTitleRun: string;
  expandBtn: string;
  collapseBtn: string;
  error: string;
  errorNoZwoContent: string;
  errorBase64Conversion: string;
  errorPostMessage: string;
  errorGeminiQuotaExceeded: string;
  errorGeminiQuotaInfo: string;
  visualizerEmpty: string;
  close: string;
  buyCoffeeText: string;
  modelInfo: string;
  tabBike: string;
  tabRun: string;
  tabBuilder: string;
  tabExplore: string;
  exploreTitle: string;
  exploreSubtitle: string;
  exploreUseTemplate: string;
  exploreLoading: string;
  exploreError: string;
  exploreEmpty: string;
  /** Friendly empty-state heading shown above the CTA when the catalog has no rows. */
  exploreEmptyTitle: string;
  /** CTA label that bounces the user back to the builder from the empty Explore tab. */
  exploreEmptyCta: string;
  exploreLangBadge: string;
  exploreRetry: string;
  /** Shown in dev when the SPA is opened on plain Vite (:5173) so Netlify functions are not mounted. */
  exploreDevFunctionsTitle: string;
  exploreDevFunctionsBody: string;
  exploreSportBike: string;
  exploreSportRun: string;
  intPace: string;
  intPower: string;
  intHR: string;
  garminSyncWarning: string;
  uploadWarning: string;
  copyText: string;
  copyCode: string;
  copied: string;
  outputHeader: string;
  contactLabel: string;
  contactEmail: string;
  viewText: string;
  viewZwo: string;
  downloadsLabel: string;
  platformTP: string;
  platformIntervals: string;
  tpInstruction: string;
  tpRunWarning: string;
  showCode: string;
  hideCode: string;
  intervalsInstruction: string;
  intervalsZwoInstruction: string;
  intervalsOr: string;
  intervalsDownload: string;
  howToGet: string;
  step1: string;
  step1Link: string;
  step2: string;
  step3: string;
  step4Pre: string;
  step4Post: string;
  signUp: string;
  logIn: string;
  passwordPlaceholder: string;
  newPasswordPlaceholder: string;
  retypeNewPasswordPlaceholder: string;
  haveAccount: string;
  noAccount: string;
  logout: string;
  loginSignUp: string;
  settingsTitle: string;
  settingsBack: string;
  settingsFtpSectionTitle: string;
  settingsCyclingFtpLabel: string;
  settingsRunningFtpLabel: string;
  settingsSaveBtn: string;
  settingsSavedToast: string;
  settingsSubscriptionSectionTitle: string;
  settingsSubscriptionStatusLabel: string;
  settingsSubscriptionExpiryLabel: string;
  settingsSupportSectionTitle: string;
  settingsSupportHint: string;
  settingsSignedInAs: string;
  loginToSaveWorkouts: string;
  loginToSaveWorkoutsLoginCta: string;
  loginToSaveWorkoutsSuffix: string;
  guestLimitExceeded: string;
  historyTab: string;
  proFeatureRequired: string;
  freeTierRemainingLabel: string;
  freeTierUpgradeCta: string;
  freeTierExhausted: string;
  upgradeToProBtn: string;
  pricingModalTitle: string;
  upgradeNeedLogin: string;
  upgradeCheckoutFailed: string;
  planPro: string;
  planFree: string;
  uses: string;
  workoutSaved: string;
  workoutGenerated: string;
  editingFromHistoryBanner: string;
  historyPromptTooltipEmpty: string;
  historyLoadError: string;
  editModeBannerTitle: string;
  editModeBannerHint: string;
  workoutActionChipsTitle: string;
  workoutActionChipsBike: WorkoutActionChipCategory[];
  workoutActionChipsRun: WorkoutActionChipCategory[];
  placeholderHints: string[];
  pricingFeatureUnlimitedWorkouts: string;
  pricingFeatureExportGarminZwift: string;
  pricingFeatureGeminiModel: string;
  /** Short PRO plan pitch for the pricing modal (replaces older bullet list). */
  pricingModalProDescription: string;
  pricingFooterNote: string;
  workoutSaveDbError: string;
  errorMissingApiKeyServer: string;
  errorAiOverloaded: string;
  errorGateway504Html: string;
  errorJobTimeout: string;
  errorJobEnqueue: string;
  errorUnauthorizedServer: string;
  errorAiInvalidResponse: string;
  intensityTargetLabel: string;
  intensityTargetPower: string;
  intensityTargetHr: string;
  intensityTargetPace: string;
  unitFtpPct: string;
  unitHrPct: string;
  unitPacePct: string;
  unitRunPowerPct: string;
  zonesMethodCustom: string;
  zonesSaveBtn: string;
  zonesSaving: string;
  zonesSavedToast: string;
  zonesSaveError: string;
  zonesLoginToSave: string;
  zonesInvalidGenerateBlocked: string;
  errZoneRange: string;
  errZoneContinuity: string;
  aiProcessingConsentLabel: string;
  aiProcessingConsentRequiredToast: string;
  aiProcessingDisclaimer: string;
  aiProcessingDisclaimerLink: string;
  cookieBannerMessage: string;
  cookieBannerAccept: string;
  cookieBannerPrivacyLink: string;
  footerCopyright: string;
  footerTermsOfService: string;
  footerPrivacyPolicy: string;
  errorTurnstileRequired: string;
  errorTurnstileFailed: string;
  errorGuestEnqueueRateLimit: string;
  errorPromptUnsafe: string;
  cloudConnectionsTitle: string;
  cloudConnectionsRefresh: string;
  cloudConnected: string;
  cloudDisconnected: string;
  cloudConnect: string;
  cloudStatusHintAuth: string;
  cloudProviderPolar: string;
  cloudProviderWahoo: string;
  workoutSendCalendarSuccess: string;
  workoutSendCalendarError: string;
  /** Polar: extension did not confirm `chrome.storage` write within timeout (bridge missing or busy). */
  polarExportBridgeAckTimeout: string;
  /** Polar: extension responded but storage write failed (see extension service worker console). */
  polarExportBridgeAckDenied: string;
  /** Polar: popup blocked — workout still stored; open Polar via link in toast. */
  polarExportPopupBlockedHint: string;
  polarExportOpenPolarFlowLink: string;
  /** Polar: no segments in memory (generate or reload workout first). */
  polarExportNoSegments: string;
  /** Polar: segments could not be turned into Polar phases. */
  polarExportNoPhases: string;
  /** Subtle hint under .zwo / .fit download actions (manual import into apps). */
  exportManualFileHint: string;
  mobileExportDesktopOnlyHint: string;
  /** Optional second footer line (SEO / triathlon positioning). */
  footerTagline: string;
  /** Marketing landing (guest `/` route). */
  landingHeroTitleBefore: string;
  landingHeroHighlight: string;
  landingHeroSubtitle: string;
  landingCtaTryFree: string;
  landingFeaturesHeading: string;
  landingFeatureFastTitle: string;
  landingFeatureFastBody: string;
  landingFeatureEcosystemTitle: string;
  landingFeatureEcosystemBody: string;
  landingFeatureLibraryTitle: string;
  landingFeatureLibraryBody: string;
  landingPricingHeading: string;
  landingFreePlanTitle: string;
  landingFreePlanPrice: string;
  landingFreeBullet1: string;
  landingFreeBullet2: string;
  landingFreeBullet3: string;
  landingFreeCta: string;
  landingProPlanTitle: string;
  landingProPlanBadge: string;
  landingProPlanPrice: string;
  landingProBullet1: string;
  landingProBullet2: string;
  landingProBullet3: string;
  landingProCta: string;
}

type LocaleJson = Omit<
  TranslationKeys,
  'example' | 'runExample' | 'workoutActionChipsBike' | 'workoutActionChipsRun'
> & {
  workoutActionChips: {
    bike: WorkoutActionChipCategory[];
    run: WorkoutActionChipCategory[];
  };
};

function hydrate(locale: LocaleJson, lang: 'en' | 'pl'): TranslationKeys {
  const { workoutActionChips, ...rest } = locale;
  return {
    ...rest,
    workoutActionChipsBike: workoutActionChips.bike,
    workoutActionChipsRun: workoutActionChips.run,
    example:
      lang === 'pl'
        ? `Przykład: "${GEMINI_CONFIG.DEFAULT_BIKE_EXAMPLE_PROMPT_PL}"`
        : `Example: "${GEMINI_CONFIG.DEFAULT_BIKE_EXAMPLE_PROMPT}"`,
    runExample:
      lang === 'pl'
        ? `Przykład: "${GEMINI_CONFIG.DEFAULT_RUN_EXAMPLE_PROMPT_PL}"`
        : `Example: "${GEMINI_CONFIG.DEFAULT_RUN_EXAMPLE_PROMPT}"`,
  };
}

export const TRANSLATIONS: { en: TranslationKeys; pl: TranslationKeys } = {
  en: hydrate(enLocale as LocaleJson, 'en'),
  pl: hydrate(plLocale as LocaleJson, 'pl'),
};

/** String-only translator for keys backed by plain strings (UI labels). */
export function createStringT(lang: 'en' | 'pl') {
  const bundle = TRANSLATIONS[lang];
  return function t(key: keyof TranslationKeys): string {
    const v = bundle[key];
    return typeof v === 'string' ? v : String(key);
  };
}
